import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { okaidia } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, HelpCircle, Bug } from "lucide-react";
import type { Task } from "../types";

interface TaskPanelProps {
  task: Task;
  className?: string;
  domainColor?: string;
  /** Плотнее типографика (мобильный лист в тренажёре) */
  compact?: boolean;
  showActionsHeader?: boolean;
  onBack?: () => void;
  onFAQ?: () => void;
  onBugReport?: () => void;
}

export const TaskPanel = ({
  task,
  className = "",
  domainColor,
  compact = false,
  showActionsHeader = false,
  onBack,
  onFAQ,
  onBugReport,
}: TaskPanelProps) => {
  const navigate = useNavigate();

  const handleDomainClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/docs?domain=${encodeURIComponent(task.domain!)}`);
  };

  const renderCode = ({
    inline,
    className,
    children,
  }: {
    inline?: boolean;
    className?: string;
    children: React.ReactNode[];
  }) => {
    const match = /language-(\w+)/.exec(className || "");
    return !inline && match ? (
      <SyntaxHighlighter
        style={okaidia}
        language={match[1]}
        PreTag="div"
        customStyle={
          compact
            ? {
                fontSize: "0.75rem",
                margin: "0.5rem 0",
                padding: "0.75rem",
                fontFamily: "var(--font-mono)",
              }
            : { fontFamily: "var(--font-mono)" }
        }
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    ) : (
      <code
        className={`bg-neutral-light rounded px-1 py-[2px] font-mono text-neutral-text ${compact ? "text-xs" : "text-sm"}`}
      >
        {children}
      </code>
    );
  };

  const renderTable = ({ children }: { children: React.ReactNode }) => (
    <div className="overflow-x-auto">
      <table
        className={`min-w-full border border-neutral-border ${compact ? "text-xs" : "text-sm"}`}
      >
        {children}
      </table>
    </div>
  );

  const renderTH = ({ children }: { children: React.ReactNode }) => (
    <th
      className={`border border-neutral-border bg-neutral-light text-left text-neutral-text ${
        compact ? "px-2 py-1.5 text-xs" : "px-3 py-2"
      }`}
    >
      {children}
    </th>
  );

  const renderTD = ({ children }: { children: React.ReactNode }) => (
    <td className={`border border-neutral-border text-neutral-text ${compact ? "px-2 py-1.5" : "px-3 py-2"}`}>
      {children}
    </td>
  );

  return (
    <div
      className={`flex flex-col h-full bg-white font-sans relative ${className}`}
    >
      {/* Одна плоская колонка без серой «подложки» — визуально как редактор и AI Tutor */}
      <div
        className={`flex flex-col flex-1 min-h-0 overflow-hidden ${
          compact ? "px-4 pb-4 pt-2" : "px-5 sm:px-6 pb-6 pt-4"
        }`}
      >
          {showActionsHeader && !compact && (
            <div className="mb-3 flex items-center justify-between gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={onBack}
                className="h-9 px-3 rounded-lg border border-neutral-border bg-white text-neutral-text hover:bg-primary/10 hover:text-primary transition-colors inline-flex items-center gap-2 text-sm font-medium"
                title="Назад к списку задач"
              >
                <ArrowLeft size={16} />
                Назад
              </button>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onFAQ}
                  className="w-9 h-9 rounded-lg border border-neutral-border bg-white text-neutral-text hover:bg-primary/10 hover:text-primary transition-colors inline-flex items-center justify-center"
                  title="FAQ"
                >
                  <HelpCircle size={16} />
                </button>
                <button
                  type="button"
                  onClick={onBugReport}
                  className="w-9 h-9 rounded-lg border border-neutral-border bg-white text-neutral-text hover:bg-primary/10 hover:text-primary transition-colors inline-flex items-center justify-center"
                  title="Сообщить о проблеме"
                >
                  <Bug size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Заголовок с номером задачи */}
          <div className={`flex-shrink-0 ${compact ? "mb-2" : "mb-4"}`}>
            <h2
              className={`font-heading font-bold text-neutral-text ${
                compact ? "text-xl mb-2" : "text-2xl mb-3"
              }`}
            >
              {task.id}. {task.title}
            </h2>
            {/* Блок с доменной областью */}
            {task.domain && (
              <div className="relative group inline-block">
                <button
                  onClick={handleDomainClick}
                  className={`inline-block px-3 py-1.5 rounded-lg transition-colors text-sm font-medium border cursor-pointer ${
                    domainColor || "bg-neutral-light text-neutral-text border-neutral-border"
                  } ${domainColor ? "hover:opacity-80" : "hover:bg-neutral-border"}`}
                  aria-describedby="domain-tooltip"
                >
                  {task.domain}
                </button>
                <div
                  id="domain-tooltip"
                  className="absolute left-full top-1/2 -translate-y-1/2 ml-2 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 whitespace-nowrap pointer-events-none"
                  role="tooltip"
                >
                  Документация по доменной области
                  <div className="absolute right-full top-1/2 -translate-y-1/2 -mr-1 w-2 h-2 bg-neutral-text rotate-45"></div>
                </div>
              </div>
            )}
          </div>

          {/* Описание задачи: занимает всю доступную высоту панели, скролл только при длинном тексте */}
          {task.description && (
            <div
              className={[
                "flex-1 min-h-0 overflow-auto pr-1 relative",
                "[scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.25)_transparent]",
                "[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent",
                "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300/35",
                "hover:[&::-webkit-scrollbar-thumb]:bg-slate-400/45",
              ].join(" ")}
            >
              <div className="pb-4">
                {/* Основное описание задачи */}
                {task.description && (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code: renderCode,
                      table: renderTable,
                      th: renderTH,
                      td: renderTD,
                      h1: ({ children }) => (
                        <h1 className={compact ? "text-xl font-heading font-bold my-1.5" : "text-2xl font-heading font-bold my-2"}>
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className={compact ? "text-lg font-heading font-semibold my-1.5" : "text-xl font-heading font-semibold my-2"}>
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className={compact ? "text-base font-heading font-semibold my-1" : "text-lg font-heading font-semibold my-1"}>
                          {children}
                        </h3>
                      ),
                      h4: ({ children }) => (
                        <h4 className={compact ? "text-sm font-heading font-semibold my-1" : "text-base font-heading font-semibold my-1"}>
                          {children}
                        </h4>
                      ),
                      h5: ({ children }) => (
                        <h5 className={compact ? "text-sm font-heading font-semibold my-0.5" : "text-sm font-heading font-semibold my-1"}>
                          {children}
                        </h5>
                      ),
                      h6: ({ children }) => (
                        <h6 className={compact ? "text-xs font-heading font-medium my-0.5" : "text-sm font-heading font-medium my-1"}>
                          {children}
                        </h6>
                      ),
                      p: ({ children }) => (
                        <p
                          className={
                            compact
                              ? "mb-2 text-sm text-neutral-text/90 leading-snug"
                              : "mb-3 text-sm text-neutral-text/90 leading-relaxed"
                          }
                        >
                          {children}
                        </p>
                      ),
                      ul: ({ children }) => (
                        <ul
                          className={
                            compact
                              ? "list-disc my-1.5 space-y-0.5 pl-5 text-sm text-neutral-text/90"
                              : "list-disc my-2 space-y-1 pl-6 text-sm text-neutral-text/90"
                          }
                        >
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol
                          className={
                            compact
                              ? "list-decimal my-1.5 space-y-0.5 pl-5 text-sm text-neutral-text/90"
                              : "list-decimal my-2 space-y-1 pl-6 text-sm text-neutral-text/90"
                          }
                        >
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => <li className={compact ? "ml-1" : "ml-2"}>{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold text-neutral-text">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                    }}
                  >
                    {task.description}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

